// React
import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// MUI components
import { Box, Typography, CircularProgress, Stack } from '@mui/material';

// Custom components
import UserAvatar from 'components/Avatar';
import CustomToolTip from 'components/Tooltip';
import PassFail from 'components/TemplateSubmissionPreview/Fields/PassFail';
import MultipleChoice from 'components/TemplateSubmissionPreview/Fields/MultipleChoice';
import Procedure from 'components/TemplateSubmissionPreview/Fields/Procedure';
import TakePhoto from 'components/TemplateSubmissionPreview/Fields/TakePhoto';
import Number from 'components/TemplateSubmissionPreview/Fields/Number';
import Text from 'components/TemplateSubmissionPreview/Fields/Text';
import Signature from 'components/TemplateSubmissionPreview/Fields/Signature';
import DateTime from 'components/TemplateSubmissionPreview/Fields/DateTime';
import Instruction from 'components/TemplateSubmissionPreview/Fields/Instruction';
import Dropdown from 'components/TemplateSubmissionPreview/Fields/Dropdown';
import GeoStamp from 'components/TemplateSubmissionPreview/Fields/GeoStamp';
import Location from 'components/TemplateSubmissionPreview/Fields/Location';
import PhotoAnnotate from 'components/TemplateSubmissionPreview/Fields/PhotoAnnotate';
import TakeVideo from 'components/TemplateSubmissionPreview/Fields/TakeVideo';
import UploadFile from 'components/TemplateSubmissionPreview/Fields/UploadFile';
import Calculation from 'components/TemplateSubmissionPreview/Fields/Calculation';

// Utils
import xeniaApi from 'api/index';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import useResponsive from 'utils/CustomHooks/useResponsive';
import { getTimeZone } from 'utils/globalFunction';
import moment from 'moment-timezone';
// Icons

import {
  ArrowIcon,
  HistoryCard,
  HistoryContainer,
  HistoryItemWrapper,
  StepAnswerContainer,
  StepAnswerItem,
} from './styles';
import ArrowNextRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CustomModal from 'components/CustomModal';
import DIALOGS from 'utils/dialogIds';
import { useDispatch, useSelector } from 'react-redux';
import selectors from 'store/selectors';
import actions from 'store/actions';
import Header from './Header';
import useTemplateBuilder from 'utils/CustomHooks/templateBuilder/useTemplateBuilder';
import { generateTemplateHashes } from 'components/TaskChecklist/utils';

// Types
// interface SubmissionHistoryProps {}

interface HistoryItemInterface {
  afterEdit: Record<string, any>;
  beforeEdit: Record<string, any>;
  changesSummary: {
    itemsUpdated: number;
    notesChanged: number;
    flagsChanged: number;
  };
  editedAt: string;
  editedBy: string;
  itemsChanged: string[];
}

const HistoryItem: React.FC<any> = (props) => {
  const { item, itemsHash } = props;
  const { editedBy, editedAt, beforeEdit, afterEdit, itemsChanged } = item;
  const itemId = itemsChanged[0];
  const hashedUserList = useSelector(selectors.getHashedUserList);
  const editedByUser = hashedUserList?.[editedBy];

  const name =
    editedByUser?.firstName + ' ' + editedByUser?.lastName || 'Anonymous';

  const beforeEditItem = beforeEdit[itemId];
  const afterEditItem = afterEdit[itemId];
  const getItem = (
    type:
      | 'header'
      | 'number'
      | 'passFail'
      | 'multipleChoice'
      | 'procedure'
      | 'textField'
      | 'takePhoto'
      | 'temperature'
      | 'calculation'
      | 'cost'
      | 'signature'
      | 'dateTime'
      | 'instruction'
      | 'dropdown'
      | 'geoStamp'
      | 'location'
      | 'photoAnnotate'
      | 'takeVideo'
      | 'uploadFile',
    item: any,
  ) => {
    const props = {
      item,
      answers: item?.answers,
      isPreview: true,
      error: '',
      hideCorrectiveTask: true,
    };

    switch (type) {
      case 'passFail':
        return <PassFail {...props} />;
      case 'multipleChoice':
        return <MultipleChoice {...props} />;
      case 'dropdown':
        return <Dropdown {...props} />;
      case 'procedure':
        return <Procedure {...props} />;
      case 'takePhoto':
        return <TakePhoto {...props} />;
      case 'number':
      case 'cost':
      case 'temperature':
        return <Number {...props} />;
      case 'calculation':
        return <Calculation {...props} hideFormula={true} />;
      case 'textField':
        return <Text {...props} />;
      case 'signature':
        return <Signature {...props} />;
      case 'dateTime':
        return <DateTime {...props} />;
      case 'instruction':
        return <Instruction {...props} />;
      case 'geoStamp':
        return <GeoStamp {...props} />;
      case 'location':
        return <Location {...props} />;
      case 'photoAnnotate':
        return <PhotoAnnotate {...props} />;
      case 'takeVideo':
        return <TakeVideo {...props} />;
      case 'uploadFile':
        return <UploadFile {...props} />;
      default:
        break;
    }
  };
  return (
    <HistoryItemWrapper>
      <Stack direction="row" gap={'17px'}>
        <UserAvatar
          width={26}
          height={26}
          userId={editedBy}
          className="avatar"
        />

        <Typography className="editText">
          {name} <span>edited</span> a response on{' '}
          {moment(editedAt).format('MMM DD, YYYY hh:mm A')}
        </Typography>
      </Stack>
      <HistoryCard>
        <Typography className="question">Question</Typography>
        <Typography className="stepDescription">
          {itemsHash?.[itemsChanged[0]]?.description || ''}
        </Typography>

        <StepAnswerContainer>
          <StepAnswerItem>
            {getItem(beforeEditItem.type, beforeEditItem) || ''}
          </StepAnswerItem>
          <ArrowIcon>
            <ArrowNextRoundedIcon />
          </ArrowIcon>
          <StepAnswerItem>
            {getItem(afterEditItem.type, afterEditItem) || ''}
          </StepAnswerItem>
        </StepAnswerContainer>
      </HistoryCard>
    </HistoryItemWrapper>
  );
};

export default HistoryItem;
